import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

// A textarea with @username autocomplete for the forum. Detects an @token at the caret, queries
// /forum/members/search, and inserts "@username " on select. Purely presentational otherwise —
// value/onChange are owned by the parent so it drops into the existing composers unchanged.
export function MentionTextarea({
  value, onChange, rows = 3, maxLength = 8000, disabled = false, placeholder = "",
  className = "", style = {}, testid, P = {},
}) {
  const ref = useRef(null);
  const boxRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [active, setActive] = useState(0);

  const cardBg = P.rowSurface || "#111213";
  const cardBorder = P.cardBorder || "#2E2F31";
  const text = P.text || "#E8E8EA";
  const textMuted = P.textMuted || "#8B8D90";
  const accent = P.lime || "#cfe86b";

  const detect = useCallback((el) => {
    const caret = el.selectionStart ?? (value || "").length;
    const upto = (value || "").slice(0, caret);
    const m = upto.match(/(?:^|\s)@([a-zA-Z0-9_]{1,30})$/);
    if (m) { setQuery(m[1]); setOpen(true); setActive(0); }
    else { setOpen(false); setQuery(""); setResults([]); }
  }, [value]);

  const handleChange = (e) => { onChange(e.target.value); detect(e.target); };

  useEffect(() => {
    if (!open || query.length < 1) { setResults([]); return; }
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/forum/members/search", { params: { q: query } });
        if (alive) { setResults(data.members || []); setActive(0); }
      } catch { if (alive) setResults([]); }
    }, 140);
    return () => { alive = false; clearTimeout(t); };
  }, [open, query]);

  const insert = (username) => {
    const el = ref.current;
    if (!el) return;
    const caret = el.selectionStart ?? (value || "").length;
    const upto = (value || "").slice(0, caret);
    const before = upto.replace(/@([a-zA-Z0-9_]{1,30})$/, `@${username} `);
    const after = (value || "").slice(caret);
    const next = before + after;
    onChange(next);
    setOpen(false); setResults([]); setQuery("");
    requestAnimationFrame(() => {
      el.focus();
      const pos = before.length;
      try { el.setSelectionRange(pos, pos); } catch { /* noop */ }
    });
  };

  const onKeyDown = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => (a + 1) % results.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => (a - 1 + results.length) % results.length); }
    else if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); insert(results[active].username); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={boxRef} className="relative">
      <textarea
        ref={ref} value={value} onChange={handleChange} onKeyDown={onKeyDown}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        rows={rows} maxLength={maxLength} disabled={disabled} placeholder={placeholder}
        data-testid={testid} className={className} style={style} />
      {open && results.length > 0 && (
        <ul data-testid="forum-mention-suggestions"
          className="absolute left-0 right-0 z-30 mt-1 max-h-60 overflow-y-auto border shadow-lg"
          style={{ background: cardBg, borderColor: cardBorder }}>
          {results.map((m, i) => (
            <li key={m.username}>
              <button type="button"
                onMouseDown={(e) => { e.preventDefault(); insert(m.username); }}
                onMouseEnter={() => setActive(i)}
                data-testid={`forum-mention-option-${m.username}`}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors"
                style={{ background: i === active ? "rgba(255,255,255,0.06)" : "transparent" }}>
                <span className="w-6 h-6 shrink-0 rounded-sm overflow-hidden border" style={{ borderColor: cardBorder, background: "#0b0c07" }}>
                  {m.avatar ? <img src={m.avatar} alt="" className="w-full h-full object-cover" />
                    : <span className="flex items-center justify-center w-full h-full text-[10px] font-mono" style={{ color: textMuted }}>{(m.username || "?")[0].toUpperCase()}</span>}
                </span>
                <span className="min-w-0">
                  <span className="block font-mono text-xs truncate" style={{ color: accent }}>@{m.username}</span>
                  {m.name && <span className="block text-[11px] truncate" style={{ color: textMuted }}>{m.name}</span>}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default MentionTextarea;
